import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import API_ENDPOINTS from '../config/api'

export interface WorkbenchSemanticAlignmentCandidate {
  candidateId: string
  sourceRowId: string
  sourceWitnessIds: string[]
  sourceText: string
  targetWitnessId: string
  targetRowId: string
  targetUnitUuid: string
  targetUnitLabel?: string | null
  targetText: string
  score: number
  scoreMargin: number
  rank: number
  placementKind: 'move-existing'
  modelId: string
  applyBlockReason?: string | null
}

export interface WorkbenchSemanticAlignmentPreview {
  previewId: string
  modelId: string
  candidates: WorkbenchSemanticAlignmentCandidate[]
  diagnostics: Record<string, unknown>
}

export interface RequestWorkbenchSemanticAlignmentPreviewInput {
  witnesses: Witness[]
  rows: AnchoredRow[]
  sourceRowIds: string[]
  sourceWitnessIds?: string[]
  targetWitnessIds?: string[]
  maxCandidatesPerRow?: number
  minScore?: number
}

const toRequestWitness = (witness: Witness) => ({
  id: witness.id,
  sigla: witness.sigla,
  language: witness.language || null,
  uploaded_file: {
    content: witness.uploaded_file?.content || '',
  },
})

const toRequestRow = (row: AnchoredRow) => ({
  id: row.id,
  unitMappings: row.unitMappings.map(mapping => ({
    witnessId: mapping.witnessId,
    unitUuid: mapping.unitUuid,
    unitLabel: mapping.unitLabel || null,
    textSnapshot: mapping.textSnapshot || '',
    excluded: Boolean(mapping.excluded),
    translationStatus: mapping.translationStatus || null,
  })),
})

const mapCandidate = (candidate: {
  candidate_id: string
  source_row_id: string
  source_witness_ids?: string[]
  source_text: string
  target_witness_id: string
  target_row_id: string
  target_unit_uuid: string
  target_unit_label?: string | null
  target_text: string
  score: number
  score_margin?: number
  rank: number
  placement_kind: 'move-existing'
  model_id: string
  apply_block_reason?: string | null
}): WorkbenchSemanticAlignmentCandidate => ({
  candidateId: candidate.candidate_id,
  sourceRowId: candidate.source_row_id,
  sourceWitnessIds: candidate.source_witness_ids || [],
  sourceText: candidate.source_text,
  targetWitnessId: candidate.target_witness_id,
  targetRowId: candidate.target_row_id,
  targetUnitUuid: candidate.target_unit_uuid,
  targetUnitLabel: candidate.target_unit_label || null,
  targetText: candidate.target_text,
  score: candidate.score,
  scoreMargin: candidate.score_margin ?? 0,
  rank: candidate.rank,
  placementKind: candidate.placement_kind,
  modelId: candidate.model_id,
  applyBlockReason: candidate.apply_block_reason || null,
})

export const requestWorkbenchSemanticAlignmentPreview = async (
  input: RequestWorkbenchSemanticAlignmentPreviewInput,
): Promise<WorkbenchSemanticAlignmentPreview> => {
  const response = await fetch(API_ENDPOINTS.workbenchSemanticAlignment.preview, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      witnesses: input.witnesses.map(toRequestWitness),
      rows: input.rows.map(toRequestRow),
      source_row_ids: input.sourceRowIds,
      source_witness_ids: input.sourceWitnessIds || [],
      target_witness_ids: input.targetWitnessIds || [],
      max_candidates_per_row: input.maxCandidatesPerRow || 3,
      min_score: input.minScore ?? 0.30,
    }),
  })

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    let message = detail || 'Failed to build semantic alignment preview'
    try {
      const payload = JSON.parse(detail) as { detail?: string }
      message = payload.detail || message
    } catch {
      // Keep the raw response text when the server did not return JSON.
    }
    throw new Error(message)
  }

  const payload = await response.json() as {
    preview_id: string
    model_id: string
    candidates?: Array<Parameters<typeof mapCandidate>[0]>
    diagnostics?: Record<string, unknown>
  }

  return {
    previewId: payload.preview_id,
    modelId: payload.model_id,
    candidates: (payload.candidates || []).map(mapCandidate),
    diagnostics: payload.diagnostics || {},
  }
}
